import { useState, useEffect } from 'react';
import api from '../utils/api';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Users, TrendUp, CaretDown, CaretUp, MagnifyingGlass, X, Robot } from '@phosphor-icons/react';
import AIGuideDrawer from '../components/AIGuideDrawer';

const DailyCheckIn = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [filteredCategoryData, setFilteredCategoryData] = useState([]);
  const [monthlyOverview, setMonthlyOverview] = useState([]);
  const [monthlyViewMonths, setMonthlyViewMonths] = useState(6); // Show last 6 months by default
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedTab, setExpandedTab] = useState('checked-in');
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    status: 'all' // all, checked-in, not-checked-in
  });
  const { activeBranch } = useBranch();
  
  // AI Guide states
  const [showAIGuide, setShowAIGuide] = useState(false);
  const [selectedGuideCustomer, setSelectedGuideCustomer] = useState(null);

  useEffect(() => {
    if (activeBranch) {
      fetchData();
    }
  }, [selectedDate, activeBranch, monthlyViewMonths]);

  useEffect(() => {
    applyFilters();
  }, [categoryData, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch overall summary, category-wise data, and monthly overview
      const [summaryResponse, categoryResponse, monthlyResponse] = await Promise.all([
        api.get(`/attendance/daily-summary`, {
          params: { 
            date: selectedDate,
            branch_id: activeBranch?.branch_id
          }
        }),
        api.get(`/attendance/category-wise-summary`, {
          params: { 
            date: selectedDate,
            branch_id: activeBranch?.branch_id
          }
        }),
        api.get(`/attendance/monthly-overview`, {
          params: {
            branch_id: activeBranch?.branch_id,
            months: monthlyViewMonths
          }
        })
      ]);
      
      console.log('Summary Response:', summaryResponse.data);
      console.log('Category Response:', categoryResponse.data);
      console.log('Monthly Response:', monthlyResponse.data);
      
      setSummary(summaryResponse.data);
      setCategoryData(categoryResponse.data.categories || []);
      setMonthlyOverview(monthlyResponse.data.monthly_data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...categoryData];

    // Category filter
    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(cat => cat.category === filters.category);
    }

    // Status filter (checked-in, not-checked-in, all)
    if (filters.status === 'checked-in') {
      filtered = filtered.filter(cat => cat.checked_in > 0);
    } else if (filters.status === 'not-checked-in') {
      filtered = filtered.filter(cat => cat.not_checked_in > 0);
    }

    // Search filter (searches within customer lists)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.map(cat => {
        const filteredCheckedIn = cat.checked_in_list?.filter(customer =>
          customer.name?.toLowerCase().includes(searchLower) ||
          customer.phone_number?.includes(searchLower) ||
          customer.reg_no?.toString().includes(searchLower)
        ) || [];

        const filteredNotCheckedIn = cat.not_checked_in_list?.filter(customer =>
          customer.name?.toLowerCase().includes(searchLower) ||
          customer.phone_number?.includes(searchLower) ||
          customer.reg_no?.toString().includes(searchLower)
        ) || [];

        return {
          ...cat,
          checked_in_list: filteredCheckedIn,
          not_checked_in_list: filteredNotCheckedIn,
          checked_in: filteredCheckedIn.length,
          not_checked_in: filteredNotCheckedIn.length,
          total: filteredCheckedIn.length + filteredNotCheckedIn.length
        };
      }).filter(cat => cat.total > 0);
    }

    setFilteredCategoryData(filtered);
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      status: 'all'
    });
  };

  const hasActiveFilters = filters.search || filters.category !== 'all' || filters.status !== 'all';

  const getCategoryColor = (category) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800 border-blue-300',
      '10 Days': 'bg-green-100 text-green-800 border-green-300',
      '30 Days': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      '60 Days': 'bg-orange-100 text-orange-800 border-orange-300',
      '90 Days': 'bg-red-100 text-red-800 border-red-300',
      '120 Days': 'bg-purple-100 text-purple-800 border-purple-300',
      'Buyers': 'bg-emerald-100 text-emerald-800 border-emerald-300',
      'Challenge Therapy': 'bg-pink-100 text-pink-800 border-pink-300'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const toggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-[#2C4C3B] mb-2">Daily Check-In Summary</h1>
          <p className="text-sm sm:text-base text-[#1C1C1A]/60">Overview of customer check-ins per day</p>
        </div>

        <div className="mb-6">
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A] mb-2 block">Select Date</Label>
          <Input 
            type="date" 
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="max-w-xs border-[#1C1C1A]/20 focus:border-[#2C4C3B] focus:ring-[#2C4C3B]"
          />
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 border-[#1C1C1A]/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#1C1C1A]/60 mb-1">Total Customers</p>
                <p className="text-3xl font-semibold text-[#2C4C3B]">{summary?.summary?.total_customers || 0}</p>
              </div>
              <Users size={40} className="text-[#2C4C3B]/30" />
            </div>
          </Card>

          <Card className="p-6 border-green-200 bg-green-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 mb-1">Checked In</p>
                <p className="text-3xl font-semibold text-green-700">{summary?.summary?.checked_in || 0}</p>
              </div>
              <CheckCircle size={40} weight="fill" className="text-green-500" />
            </div>
          </Card>

          <Card className="p-6 border-red-200 bg-red-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 mb-1">Not Checked In</p>
                <p className="text-3xl font-semibold text-red-700">{summary?.summary?.not_checked_in || 0}</p>
              </div>
              <XCircle size={40} weight="fill" className="text-red-500" />
            </div>
          </Card>

          <Card className="p-6 border-[#1C1C1A]/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#1C1C1A]/60 mb-1">Attendance Rate</p>
                <p className="text-3xl font-semibold text-[#2C4C3B]">{summary?.summary?.attendance_rate || 0}%</p>
              </div>
              <TrendUp size={40} className="text-[#2C4C3B]/30" />
            </div>
          </Card>
        </div>

        {/* Tabs for Overall and Category-wise */}
        <Tabs defaultValue="overall" className="w-full">
          <TabsList className="bg-[#2C4C3B]/10 mb-6">
            <TabsTrigger value="overall" className="data-[state=active]:bg-[#2C4C3B] data-[state=active]:text-white">
              Overall List
            </TabsTrigger>
            <TabsTrigger value="category-wise" className="data-[state=active]:bg-[#2C4C3B] data-[state=active]:text-white">
              Category-wise Breakdown
            </TabsTrigger>
            <TabsTrigger value="monthly" className="data-[state=active]:bg-[#2C4C3B] data-[state=active]:text-white">
              Monthly Overview
            </TabsTrigger>
          </TabsList>

          {/* Overall Tab - Original Daily Check-In Content */}
          <TabsContent value="overall">
            <Tabs defaultValue="checked-in" className="w-full">
              <TabsList className="bg-[#2C4C3B]/10 mb-6">
                <TabsTrigger value="checked-in" className="data-[state=active]:bg-[#2C4C3B] data-[state=active]:text-white">
                  Checked In ({summary?.summary?.checked_in || 0})
                </TabsTrigger>
                <TabsTrigger value="not-checked-in" className="data-[state=active]:bg-[#2C4C3B] data-[state=active]:text-white">
                  Not Checked In ({summary?.summary?.not_checked_in || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="checked-in">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-[#2C4C3B] mb-4">Customers Who Checked In</h3>
                  {summary?.checked_in_list?.length === 0 ? (
                    <p className="text-center py-8 text-[#1C1C1A]/60">No customers checked in on this date</p>
                  ) : (
                    <CustomerTable 
                      customers={summary?.checked_in_list || []} 
                      emptyMessage="No customers checked in" 
                      getCategoryColor={getCategoryColor}
                      onAIGuide={(customer) => {
                        setSelectedGuideCustomer({ id: customer.customer_id, name: customer.name });
                        setShowAIGuide(true);
                      }}
                    />
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="not-checked-in">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-[#2C4C3B] mb-4">Customers Who Did Not Check In</h3>

                  {summary?.not_checked_in_list?.length === 0 ? (
                    <p className="text-center py-8 text-[#1C1C1A]/60">All customers checked in today!</p>
                  ) : (
                    <CustomerTable 
                      customers={summary?.not_checked_in_list || []} 
                      emptyMessage="All checked in" 
                      getCategoryColor={getCategoryColor}
                      onAIGuide={(customer) => {
                        setSelectedGuideCustomer({ id: customer.customer_id, name: customer.name });
                        setShowAIGuide(true);
                      }}
                    />
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Category-wise Tab */}
          <TabsContent value="category-wise">
            <Card className="p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#2C4C3B]">Summary by Category</h3>
                {hasActiveFilters && (
                  <span className="text-sm text-[#1C1C1A]/60">
                    Showing {filteredCategoryData.length} of {categoryData.length} categories
                  </span>
                )}
              </div>
              
              {filteredCategoryData.length === 0 ? (
                <p className="text-center py-8 text-[#1C1C1A]/60">
                  {hasActiveFilters ? 'No results match your filters' : 'No data available for this date'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-[#1C1C1A]/10">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Category</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Total</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Checked In</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Not Checked In</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Rate</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategoryData.map((cat) => (
                        <tr key={cat.category} className="border-b border-[#1C1C1A]/5 hover:bg-[#2C4C3B]/5">
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(cat.category)}`}>
                              {cat.category}
                            </span>
                          </td>
                          <td className="text-center py-3 px-4 text-base font-semibold text-[#1C1C1A]">{cat.total}</td>
                          <td className="text-center py-3 px-4">
                            <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                              <CheckCircle size={18} weight="fill" className="text-green-600" />
                              {cat.checked_in}
                            </span>
                          </td>
                          <td className="text-center py-3 px-4">
                            <span className="inline-flex items-center gap-1 text-red-700 font-medium">
                              <XCircle size={18} weight="fill" className="text-red-600" />
                              {cat.not_checked_in}
                            </span>
                          </td>
                          <td className="text-center py-3 px-4 text-base font-semibold text-[#2C4C3B]">{cat.attendance_rate}%</td>
                          <td className="text-center py-3 px-4">
                            <button
                              onClick={() => toggleCategory(cat.category)}
                              className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-[#2C4C3B] text-white hover:bg-[#2C4C3B]/90 text-sm"
                            >
                              View
                              {expandedCategory === cat.category ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Expanded Category Details */}
            {expandedCategory && (
              <Card className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-[#2C4C3B]">{expandedCategory} - Customer Details</h3>
                  <button onClick={() => setExpandedCategory(null)} className="text-sm text-[#1C1C1A]/60 hover:text-[#1C1C1A]">Close</button>
                </div>

                <div className="flex gap-2 mb-4 border-b border-[#1C1C1A]/10">
                  <button
                    onClick={() => setExpandedTab('checked-in')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      expandedTab === 'checked-in' ? 'text-[#2C4C3B] border-b-2 border-[#2C4C3B]' : 'text-[#1C1C1A]/60 hover:text-[#1C1C1A]'
                    }`}
                  >
                    Checked In ({categoryData.find(c => c.category === expandedCategory)?.checked_in || 0})
                  </button>
                  <button
                    onClick={() => setExpandedTab('not-checked-in')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      expandedTab === 'not-checked-in' ? 'text-[#2C4C3B] border-b-2 border-[#2C4C3B]' : 'text-[#1C1C1A]/60 hover:text-[#1C1C1A]'
                    }`}
                  >
                    Not Checked In ({categoryData.find(c => c.category === expandedCategory)?.not_checked_in || 0})
                  </button>
                </div>

                <div className="overflow-x-auto">
                  {expandedTab === 'checked-in' ? (
                    <CustomerTable 
                      customers={categoryData.find(c => c.category === expandedCategory)?.checked_in_list || []}
                      emptyMessage="No customers checked in"
                      getCategoryColor={getCategoryColor}
                    />
                  ) : (
                    <CustomerTable 
                      customers={categoryData.find(c => c.category === expandedCategory)?.not_checked_in_list || []}
                      emptyMessage="All customers checked in today!"
                      getCategoryColor={getCategoryColor}
                    />
                  )}
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Monthly Overview Tab */}
          <TabsContent value="monthly">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#2C4C3B]">Monthly Check-In Overview</h3>
                  <p className="text-sm text-[#1C1C1A]/60">Customer check-ins and attendance trends per month</p>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-[#1C1C1A]/70">Show Last</Label>
                  <Select value={monthlyViewMonths.toString()} onValueChange={(value) => setMonthlyViewMonths(parseInt(value))}>
                    <SelectTrigger className="w-32 border-[#1C1C1A]/20 focus:border-[#2C4C3B] focus:ring-[#2C4C3B]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 Months</SelectItem>
                      <SelectItem value="6">6 Months</SelectItem>
                      <SelectItem value="12">12 Months</SelectItem>
                      <SelectItem value="24">24 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {monthlyOverview.length === 0 ? (
                <p className="text-center py-8 text-[#1C1C1A]/60">No monthly data available</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-[#1C1C1A]/10">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Month</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Total Customers</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Total Check-Ins</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Unique Customers</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Avg Check-Ins/Day</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Attendance Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyOverview.map((month, index) => (
                        <tr key={index} className="border-b border-[#1C1C1A]/5 hover:bg-[#2C4C3B]/5">
                          <td className="py-3 px-4">
                            <div className="font-medium text-[#1C1C1A]">{month.month_name}</div>
                            <div className="text-xs text-[#1C1C1A]/60">{month.year}</div>
                          </td>
                          <td className="text-center py-3 px-4 text-base font-semibold text-[#1C1C1A]">
                            {month.total_customers}
                          </td>
                          <td className="text-center py-3 px-4 text-base font-semibold text-[#2C4C3B]">
                            {month.total_checkins}
                          </td>
                          <td className="text-center py-3 px-4 text-base text-[#1C1C1A]">
                            {month.unique_customers}
                          </td>
                          <td className="text-center py-3 px-4 text-base text-[#1C1C1A]">
                            {month.avg_checkins_per_day}
                          </td>
                          <td className="text-center py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              month.attendance_rate >= 70 ? 'bg-green-100 text-green-800 border border-green-300' :
                              month.attendance_rate >= 50 ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                              'bg-red-100 text-red-800 border border-red-300'
                            }`}>
                              {month.attendance_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* AI Guide Drawer */}
      <AIGuideDrawer
        isOpen={showAIGuide}
        onClose={() => {
          setShowAIGuide(false);
          setSelectedGuideCustomer(null);
        }}
        customerId={selectedGuideCustomer?.id}
        customerName={selectedGuideCustomer?.name}
      />
    </Layout>
  );
};

// Customer Table Component
const CustomerTable = ({ customers, emptyMessage, getCategoryColor, onAIGuide }) => {
  if (customers.length === 0) {
    return <p className="text-center py-8 text-[#1C1C1A]/60">{emptyMessage}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#1C1C1A]/10">
            <th className="text-left py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Reg No</th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Name</th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Phone</th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Category</th>
            <th className="text-center py-3 px-4 text-sm font-semibold text-[#2C4C3B]">Actions</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => (
            <tr key={customer.customer_id} className="border-b border-[#1C1C1A]/5 hover:bg-[#2C4C3B]/5">
              <td className="py-3 px-4 text-sm text-[#1C1C1A]">{customer.reg_no}</td>
              <td className="py-3 px-4 text-sm font-medium text-[#1C1C1A]">{customer.name}</td>
              <td className="py-3 px-4 text-sm text-[#1C1C1A]">{customer.phone_number}</td>
              <td className="py-3 px-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(customer.customer_category)}`}>
                  {customer.customer_category}
                </span>
              </td>
              <td className="py-3 px-4 text-center">
                <Button
                  size="sm"
                  onClick={() => onAIGuide(customer)}
                  className="bg-teal-500 hover:bg-teal-600 text-white text-xs"
                >
                  <Robot size={14} className="mr-1" weight="fill" />
                  AI Guide
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DailyCheckIn;
